import { useState, useEffect } from 'react';
import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import ProfileSubNav from '../components/shared/ProfileSubNav';
import { useLang } from '../i18n';
import { useGeo } from '../geo';

const API = process.env.REACT_APP_API_BASE_URL;

const css = `
  .tx { padding: 6px 16px 24px; }
  .tx-tabs { display: flex; gap: 6px; background: #16142e; border-radius: 11px; padding: 4px; }
  .tx-tab { flex: 1; height: 34px; border-radius: 8px; color: #9b9ab0; font-size: 12px; font-weight: 700; }
  .tx-tab.on { background: linear-gradient(90deg, #8187f6, #76e1ff); color: #fff; }
  .tx-row { display: flex; align-items: center; gap: 12px; padding: 13px 2px; box-shadow: inset 0 -1px 0 rgba(255,255,255,.06); }
  .tx-ic { width: 38px; height: 38px; border-radius: 11px; display: grid; place-items: center; flex: none; }
  .tx-ic.in { background: rgba(0,210,106,.14); }
  .tx-ic.out { background: rgba(129,135,246,.16); }
  .tx-main { min-width: 0; flex: 1; }
  .tx-label { font-size: 14px; font-weight: 600; color: #fff; }
  .tx-date { font-size: 11px; color: #757490; margin-top: 3px; }
  .tx-right { text-align: right; flex: none; }
  .tx-amt { font-size: 14px; font-weight: 700; white-space: nowrap; }
  .tx-amt.in { color: #00d26a; }
  .tx-amt.out { color: #fff; }
  .tx-status { font-size: 10px; font-weight: 700; margin-top: 4px; }
  .tx-status.done { color: #00d26a; }
  .tx-status.pending { color: #f6c344; }
  .tx-status.failed { color: #ff5d6c; }
  .tx-summary { display: flex; gap: 8px; margin-bottom: 10px; }
  .tx-sum-card { flex: 1; padding: 10px 12px; border-radius: 10px; background: #16142e; box-shadow: inset 0 0 0 1px rgba(255,255,255,.07); }
  .tx-sum-k { font-size: 10px; color: #757490; }
  .tx-sum-v { font-size: 14px; font-weight: 700; color: #fff; margin-top: 3px; }
  .tx-sum-v.green { color: #00d26a; }
  .tx-sum-v.red { color: #ff5d6c; }
  .tx-empty { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 48px 20px; color: #5a5972; font-size: 13px; }
`;

function Arrow({ dir }) {
  return dir === 'in'
    ? <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 3v10M5 9.5l4 4 4-4" stroke="#00d26a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
    : <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 15V5M5 8.5l4-4 4 4" stroke="#8187f6" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        <circle cx="14" cy="14" r="11" stroke="rgba(255,255,255,.12)" strokeWidth="2.5" />
        <path d="M14 3a11 11 0 0110.18 6.85" stroke="#76e1ff" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const STATUS_CLS = { completed: 'done', pending: 'pending', cancelled: 'failed' };

export default function Transactions() {
  const { t } = useLang();
  const { symbol, rate } = useGeo().prices;
  const [user]   = useState(() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } });
  const [tab, setTab]       = useState('all');
  const [txs, setTxs]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    fetch(`${API}/api/user/historique.php?user_id=${user.id}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setTxs(data.data?.transactions || []);
          setSummary(data.data?.summary || null);
        } else {
          setError(data.message || t('transactions.loadError'));
        }
      })
      .catch(() => setError(t('transactions.loadError')))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const list = txs.filter((t) =>
    tab === 'all' || (tab === 'in' ? t.type === 'deposit' : t.type === 'withdrawal')
  );

  return (
    <PhoneShell activeTab="">
      <style>{css}</style>
      <BackHeader title={t('transactions.title')} />
      <ProfileSubNav />
      <div className="tx">
        {summary && (
          <div className="tx-summary">
            <div className="tx-sum-card">
              <div className="tx-sum-k">{t('transactions.deposits')}</div>
              <div className="tx-sum-v green">+{(parseFloat(summary.total_deposits || 0) * rate).toFixed(2)} {symbol}</div>
            </div>
            <div className="tx-sum-card">
              <div className="tx-sum-k">{t('transactions.withdrawals')}</div>
              <div className="tx-sum-v red">−{(parseFloat(summary.total_withdrawals || 0) * rate).toFixed(2)} {symbol}</div>
            </div>
            {summary.pending_count > 0 && (
              <div className="tx-sum-card">
                <div className="tx-sum-k">{t('transactions.pending')}</div>
                <div className="tx-sum-v">{summary.pending_count}</div>
              </div>
            )}
          </div>
        )}

        <div className="tx-tabs">
          {[['all', t('transactions.all')], ['in', t('transactions.deposits')], ['out', t('transactions.withdrawals')]].map(([id, l]) => (
            <button key={id} className={'tx-tab' + (tab === id ? ' on' : '')} onClick={() => setTab(id)}>{l}</button>
          ))}
        </div>

        {loading ? <Spinner /> : error ? (
          <div className="tx-empty">{error}</div>
        ) : list.length === 0 ? (
          <div className="tx-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path d="M7 7h11l-2.5-2.5M17 17H6l2.5 2.5" stroke="#5a5972" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('transactions.noTransactions')}
          </div>
        ) : (
          <div style={{ marginTop: 6 }}>
            {list.map((tx, i) => {
              const dir = tx.type === 'deposit' ? 'in' : 'out';
              const amt = parseFloat(tx.amount || 0);
              const stCls = STATUS_CLS[tx.status] || 'pending';
              const stLabel = t('transactions.' + (tx.status || 'pending'));
              return (
                <div className="tx-row" key={tx.id || i}>
                  <span className={'tx-ic ' + dir}><Arrow dir={dir} /></span>
                  <div className="tx-main">
                    <div className="tx-label">{tx.title || (dir === 'in' ? t('transactions.depositLabel') : t('transactions.withdrawalLabel'))}</div>
                    <div className="tx-date">{tx.description ? tx.description + ' · ' : ''}{formatDate(tx.date)}</div>
                  </div>
                  <div className="tx-right">
                    <div className={'tx-amt ' + dir}>{dir === 'in' ? '+' : '−'}{(Math.abs(amt) * rate).toFixed(2)} {symbol}</div>
                    <div className={'tx-status ' + stCls}>{stLabel}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PhoneShell>
  );
}
